import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react';
import { DateTime, Interval } from 'luxon';

import Link from '@material-ui/core/Link';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import EditIcon from '@material-ui/icons/EditOutlined';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';

import { formatAmountValue, fractionToPercent, pluralize, sqlDateToLabel } from 'helpers/helpers';

import { ExternalProductData, ExtProductCategory, ExtProductFreq, ExtProductUsage } from './model';
import { ExternalProductsStore } from 'stores/workflowStores/finances/externalProducts/externalProducts';
import { EdwardColors } from 'enums';
import colors from 'components/PortfolioTables/colors';
import useStyles, { useExtProdStyles } from '../styles/moduleStyles';
import { grayscale } from 'helpers/chart';
import { typeIcon } from 'pages/Accounts/Edit/components/iconHelper';

interface Props {
  data: ExternalProductData;
  store?: ExternalProductsStore;
  readOnly?: boolean;
  userMode?: boolean;
  detail?: boolean;
  refresh?: () => void;
  editProduct?: (productId: string) => void;
  deleteProduct?: (productId: string) => void;
  plan?: boolean;
  pdf?: boolean;
  original?: boolean;
  category: ExtProductCategory;
}

export const getUpdateColor = (lastUpdate: string) => {
  const now = DateTime.now();
  const update = DateTime.fromSQL(lastUpdate);
  const diff = Interval.fromDateTimes(update, now);
  const months = diff.length('months');
  if (months > 24) return EdwardColors.ALERT;
  if (months > 12) return EdwardColors.WARNING;
  return EdwardColors.GREEN;
};

const ExternalProduct: React.FC<Props> = ({
  data,
  store,
  readOnly,
  refresh,
  editProduct,
  deleteProduct,
  userMode,
  detail,
  plan,
  pdf,
  original,
  category,
}) => {
  const { t } = useTranslation();
  const classes = useExtProdStyles({ detail, plan, pdf, category });
  const moduleClasses = useStyles();
  const [showDetail, setShowDetail] = useState(pdf);

  const updated = () => (
    <div className={classes.line}>
      <span>
        {t(
          `externalProduct_lastUpdate${category === ExtProductCategory.PROPERTY ? '_property' : ''}${
            category === ExtProductCategory.LOAN ? '_loan' : ''
          }`,
        )}
      </span>
      <span
        style={{
          color: original ? '#333' : data.invalidLink ? EdwardColors.ALERT : getUpdateColor(data.lastUpdate),
        }}
      >
        {data.invalidLink
          ? t('externalProduct_account_invalid')
          : data.financialInstitution?.internal && data.accountId
          ? t('externalProduct_lastUpdate_daily')
          : sqlDateToLabel(data.lastUpdate, 'dd.MM.yyyy')}
      </span>
    </div>
  );

  const color =
    category === ExtProductCategory.PROPERTY ? colors[`PROPERTY_TYPE_${data.type}`] : data.financialInstitution?.colour;

  return (
    <>
      <div className={`${classes.card} ${showDetail ? '' : 'collapsed'} ${pdf ? '_PDF' : ''}`}>
        <span
          style={{
            backgroundColor: original ? grayscale(color) : color,
          }}
        />

        <div>
          <div className={`${classes.line} _${category}`}>
            <span>
              {[ExtProductCategory.EDWARD, ExtProductCategory.FINANCIAL_PRODUCT].includes(category) ? (
                <img
                  src={`data:image/png;base64,${data.financialInstitution?.logo}`}
                  alt=""
                  style={original ? { filter: 'grayscale(100%)' } : undefined}
                />
              ) : (
                <span className={moduleClasses.icon}>
                  {typeIcon(data.type, original ? EdwardColors.GRAY_MEDIUM : undefined)}
                </span>
              )}
            </span>
            {editProduct && deleteProduct && !userMode && (
              <>
                <span className="_actions">
                  <IconButton
                    size="medium"
                    className="account-delete-button"
                    onClick={() => editProduct(data.id)}
                    disabled={readOnly}
                  >
                    <EditIcon fontSize="inherit" />
                  </IconButton>

                  <IconButton
                    size="medium"
                    className="account-delete-button"
                    onClick={() => deleteProduct(data.id)}
                    disabled={readOnly}
                  >
                    <DeleteIcon fontSize="inherit" />
                  </IconButton>
                </span>
              </>
            )}
          </div>
          {!pdf ? (
            <Link
              className={moduleClasses.showLink}
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                setShowDetail(!showDetail);
              }}
            >
              <div className={classes.line}>
                <span className={classes.title}>
                  <Typography variant="h3">{data.title}</Typography>
                  <ExpandMoreIcon
                    className={`${moduleClasses.showIcon} ${showDetail ? moduleClasses.shownIcon : ''}`}
                  />
                </span>
              </div>
            </Link>
          ) : (
            <div className={classes.line}>
              <span className={classes.titlePdf}>
                <Typography variant="h3">{data.title}</Typography>
              </span>
            </div>
          )}
          <div className={classes.line}>
            <span
              dangerouslySetInnerHTML={{
                __html: t(`externalProduct_balance_alt_${category}`, {
                  interpolation: { escapeValue: false },
                }),
              }}
            />
            <span className={classes.balance}>
              {formatAmountValue(
                Math.round(data.balance.value) * (category === ExtProductCategory.LOAN ? -1 : 1),
                data.balance.currency,
              )}
            </span>
          </div>

          {[ExtProductCategory.LOAN, ExtProductCategory.PROPERTY].includes(category) && updated()}

          {data.periodic !== undefined && (
            <div className={classes.line}>
              <span>
                {t(
                  `externalProduct_periodic_alt${category === ExtProductCategory.PROPERTY ? '_property' : ''}${
                    category === ExtProductCategory.LOAN ? '_loan' : ''
                  }`,
                )}
              </span>
              <span>
                {formatAmountValue(Math.round(data.periodic.value!.value), data.periodic.value!.currency)}
                {data.periodic.value!.value > 0 && (
                  <>
                    {category === ExtProductCategory.PROPERTY && (
                      <> ({t(`general_frequency_${data.periodic.frequency}`)})</>
                    )}
                    {category === ExtProductCategory.LOAN && <> ({t(`general_frequency_MONTHLY`)})</>}
                  </>
                )}
              </span>
            </div>
          )}
          {data.employer !== undefined && (
            <div className={classes.line}>
              <span>{t('externalProduct_employer_alt')}</span>
              <span>{formatAmountValue(Math.round(data.employer.value!.value), data.employer.value!.currency)}</span>
            </div>
          )}
          {data.government !== undefined && (
            <div className={classes.line}>
              <span>{t('externalProduct_government')}</span>
              <span>
                {formatAmountValue(Math.round(data.government.value!.value), data.government.value!.currency)}
              </span>
            </div>
          )}
          <div className={classes.line}>
            <span>
              {t(
                `externalProduct_interest${category === ExtProductCategory.PROPERTY ? '_property' : ''}${
                  category === ExtProductCategory.LOAN ? '_loan_alt' : ''
                }`,
              )}
            </span>
            <span>{fractionToPercent(data.interest, 2)}</span>
          </div>

          {![ExtProductCategory.LOAN, ExtProductCategory.PROPERTY].includes(category) && updated()}

          {category === ExtProductCategory.LOAN && (
            <>
              <div className={classes.line}>
                <span>{t(`externalProduct_loan_since`)}</span>
                <span>{sqlDateToLabel(data.periodic!.since!, 'dd.MM.yyyy')}</span>
              </div>
              <div className={classes.line}>
                <span>{t(`externalProduct_loan_until`)}</span>
                <span>{sqlDateToLabel(data.periodic!.until!, 'dd.MM.yyyy')}</span>
              </div>
            </>
          )}
        </div>

        {category !== ExtProductCategory.LOAN && (
          <div className={classes.usage}>
            <div className={classes.line}>
              <span>
                <Typography variant="h4">{t('externalProduct_usage')}</Typography>
              </span>
            </div>

            <div className={classes.line}>
              <span>
                {data.frequency === ExtProductFreq.INFINITE
                  ? t(`externalProduct_freq_${ExtProductFreq.INFINITE}`)
                  : t(`externalProduct_usage_${data.usage}`)}
              </span>
              <span>{data.usage === ExtProductUsage.EXTERNAL && data.targetTitle}</span>
            </div>

            {data.frequency !== ExtProductFreq.INFINITE && data.usage !== ExtProductUsage.NONE ? (
              <>
                <div className={classes.line}>
                  <span>{t('externalProduct_freq')}</span>
                  <span>
                    {![
                      ExtProductFreq.MONTHLY,
                      ExtProductFreq.YEARLY,
                      ExtProductFreq.REPEAT_EVERY,
                      ExtProductFreq.PENSION,
                      ExtProductFreq.ADVANCE_PENSION,
                    ].includes(data.frequency) ? (
                      <>{sqlDateToLabel(data.since)}</>
                    ) : (
                      <>
                        {t('general_since')}: {sqlDateToLabel(data.since)} {t('general_until')}:{' '}
                        {sqlDateToLabel(data.until)}
                      </>
                    )}
                  </span>
                </div>

                <div className={classes.line}>
                  <span>
                    {t(`externalProduct_freq_${data.frequency}`)}
                    {data.frequency === ExtProductFreq.REPEAT_EVERY && (
                      <>
                        {' '}
                        {data.repeatYears}
                        {' '}
                        {pluralize(data.repeatYears, t('general_repeatYear'), t('general_repeatYears'))}
                      </>
                    )}
                  </span>
                  <span>
                    {data.value && data.value.value !== 0 && formatAmountValue(data.value.value, data.value.currency)}
                  </span>
                </div>
              </>
            ) : (
              <>
                <div className={classes.dummyLine} />
                <div className={classes.dummyLine} />
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default observer(ExternalProduct);
